import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { Subscription } from 'rxjs';
import { filterPlaintiffNames, findPlaintiffOption } from '@ee/common/utils';

/** The v2 intake key of the case plaintiff (`intake-form-adapter`), copied to `company_name` on submit. */
export const PLAINTIFF_FIELD_KEY = 'plaintiff';

/**
 * Searchable plaintiff-name dropdown (EE-367).
 *
 * `savedNames` are the client account's plaintiff options. The control value is what the case
 * stores. When `restricted`, text that matches no option is cleared when the user leaves the field,
 * so only an option can be saved; otherwise free text is allowed. This component never changes the
 * account list.
 */
@Component({
  selector: 'ee-plaintiff-name-field',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatAutocompleteModule],
  template: `
    <mat-form-field class="plaintiff-name-field" [class.compact]="compact">
      <mat-label>{{ label }}</mat-label>
      <input #plaintiffInput matInput [formControl]="control" [required]="required"
             [matAutocomplete]="plaintiffAuto" autocomplete="off" (blur)="onBlur()" />
      <!--
        Restricted: not Material's requireSelection, which keeps typed text out of the control (so the
        list could not narrow) and clears an exact name typed rather than clicked. Typing filters as
        usual, Enter takes the first match, and leaving the field keeps only a listed name.
      -->
      <mat-autocomplete #plaintiffAuto="matAutocomplete" [autoActiveFirstOption]="restricted"
                        (closed)="onPanelClosed()">
        <!-- First, so the highlighted option (Enter) keeps an existing case's own plaintiff. -->
        @if (offListShown) {
          <mat-option [value]="existingValue">{{ existingValue }} <span class="off-list">(not on list)</span></mat-option>
        }
        @for (option of filtered; track option) {
          <mat-option [value]="option">{{ option }}</mat-option>
        }
      </mat-autocomplete>
      @if (control.hasError('required')) {
        <mat-error>A plaintiff is required.</mat-error>
      }
      @if (hintText) {
        <mat-hint>{{ hintText }}</mat-hint>
      }
    </mat-form-field>
  `,
  styles: [`
    :host { display: block; width: 100%; }
    .plaintiff-name-field { width: 100%; }
    .off-list { opacity: 0.6; font-size: 0.85em; }
  `]
})
export class PlaintiffNameFieldComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) control!: FormControl;

  @Input() savedNames: readonly string[] = [];

  /**
   * Only a value from `savedNames` (or `existingValue`) can be kept: leaving the field snaps an exact
   * name (any case), or a partial matching exactly one name, to the listed spelling and clears anything else.
   */
  @Input() restricted = false;

  /**
   * The plaintiff an existing case already has. Offered, marked "(not on list)", when it is not one
   * of `savedNames`, so editing an old case never loses or blocks its plaintiff.
   */
  @Input() existingValue: string | null = null;

  @Input() label = 'Plaintiff Name';

  @Input() required = false;

  @Input() compact = false;

  @Input() hint = '';

  protected filtered: string[] = [];

  protected offListShown = false;

  private sub?: Subscription;

  private readonly cdr = inject(ChangeDetectorRef);

  @ViewChild(MatAutocompleteTrigger) private trigger?: MatAutocompleteTrigger;

  @ViewChild('plaintiffInput') private input?: ElementRef<HTMLInputElement>;

  protected get hintText(): string {
    if (!this.restricted) {
      return this.hint;
    }
    return [this.hint, 'Pick a plaintiff from the list.'].filter(Boolean).join(' ');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['control']) {
      this.sub?.unsubscribe();
      this.sub = this.control?.valueChanges.subscribe(() => this.refresh());
    }
    if (changes['savedNames'] || changes['control'] || changes['existingValue']) {
      this.refresh();
    }
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  protected onBlur(): void {
    // A click on an option blurs the input first; the panel's (closed) handles that path.
    if (!this.trigger?.panelOpen) {
      this.enforceSelection();
    }
  }

  /**
   * The panel also closes while the user is still typing, as soon as no option matches. Only enforce
   * once focus has left the input (Tab, a click elsewhere, or an option picked by mouse).
   */
  protected onPanelClosed(): void {
    if (this.input?.nativeElement !== document.activeElement) {
      this.enforceSelection();
    }
  }

  protected enforceSelection(): void {
    if (!this.restricted || !this.control) {
      return;
    }
    const value = typeof this.control.value === 'string' ? this.control.value : '';
    if (!value.trim()) {
      // Whitespace passes Validators.required, so it must not survive as a "plaintiff".
      if (value) {
        this.control.setValue(null);
        this.control.markAsDirty();
      }
      return;
    }
    // An existing case's own plaintiff is kept exactly as stored, even if a listed name differs only by case.
    if (this.existingValue && value.trim() === this.existingValue.trim()) {
      return;
    }
    const allowed = this.existingValue ? [...this.savedNames, this.existingValue] : this.savedNames;
    // An exact name (any case) takes its listed spelling; a partial that matches one name takes that one.
    const narrowed = filterPlaintiffNames(allowed, value);
    const match = findPlaintiffOption(allowed, value) ?? (narrowed.length === 1 ? narrowed[0] : null);
    if (match !== value) {
      this.control.setValue(match);
      this.control.markAsDirty();
    }
  }

  private refresh(): void {
    const query = typeof this.control?.value === 'string' ? this.control.value : '';
    const existing = this.existingValue?.trim();
    // The case's own stored plaintiff is a selection, not a search: offer every name alongside it.
    const holdsExisting = !!existing && query.trim().toLowerCase() === existing.toLowerCase();
    this.filtered = filterPlaintiffNames(this.savedNames, holdsExisting ? '' : query);
    this.offListShown = !!existing && !findPlaintiffOption(this.savedNames, existing) &&
      (holdsExisting || !query.trim() || existing.toLowerCase().includes(query.trim().toLowerCase()));
    this.cdr.markForCheck();
  }
}
